/* eslint-disable no-unused-vars */
import { Button, Input, Spin, Tag } from "antd";
import axios from "axios";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { AiOutlineEye } from "react-icons/ai";
import { BiCopy, BiEdit } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { DataTable } from "@/components";
import { userRole } from "@/constant";
import MetaDashboardModal from "./redesign/MetaDashboardModal";
import { flare } from "@flareapp/js";

const DashboardList = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { userDetails, business } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [limit, setLimit] = useState(Number(searchParams.get("limit")) || 10);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [mode, setMode] = useState(searchParams.get("mode") || "");
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [activeDashboardId, setActiveDashboardId] = useState("");
  const [isLimitOver, setIsLimitOver] = useState(false);
  const [warnMsg, setWarnMsg] = useState("");

  // ponytail: track location.search string instead of searchParams object to prevent infinite re-render loops
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setLimit(Number(params.get("limit")) || 10);
    setPage(Number(params.get("page")) || 1);
    setMode(params.get("mode") || "");
  }, [location.search]);

  const handleCloseModal = (val) => {
    // ponytail: close modal and clean URL if mode=create was present
    if (!val) {
      setMode("");
      if (location.search.includes("mode=")) {
        navigate("/dashboard-list", { replace: true });
      }
    } else {
      setMode(val);
    }
  };

  const getActiveDashboard = async () => {
    try {
      const response = await api.get(endpoint.active_dashboard, {
        withCredentials: true,
      });
      setActiveDashboardId(response?.result || "");
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const getDashboardList = async () => {
    const params = {
      limit,
      page,
      searchText,
    };

    if (userDetails.role !== userRole.superAdmin) {
      params.business = business;
    }
    try {
      setIsLoading(true);
      const response = await api.get(endpoint.get_dashboard_list, {
        withCredentials: true,
        params,
      });
      setData(response.data);
      setTotal(response.count);
    } finally {
      setIsLoading(false);
    }
  };

  const getMySubscription = async () => {
    try {
      const { hasAccess, assignedPlan } = await api.get(endpoint.mySubscription, {
        params: {
          type: "dashboard",
          unknown: true,
        },
      });
      if (mode !== "edit" && assignedPlan?._id) {
        setIsLimitOver(!hasAccess);
        if (!hasAccess) {
          setWarnMsg(
            `${assignedPlan?.business?.name} (${assignedPlan?.business?.id})` + " has exceeded the limit for the maximum number of Dashboard.",
          );
        } else if (!assignedPlan?.isCreateDashboardAllow) {
          setWarnMsg(`${assignedPlan?.business?.name} (${assignedPlan?.business?.id})` + " has no permission to create a dashboard.");
        } else {
          setWarnMsg("");
        }
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  // ponytail: remove mode from dependencies so opening/closing modal does not trigger redundant dashboard list fetches
  useEffect(() => {
    getDashboardList();
  }, [page, limit, business, searchText]);

  useEffect(() => {
    getActiveDashboard();
    if (userDetails?.role !== userRole.superAdmin) {
      getMySubscription();
    }
    document.title = `${appConfig.name} - Dashboard List`;
  }, []);

  const LimitedTags = ({ list, limit = 3 }) => (
    <>
      {Array.isArray(list)
        ? list.map((item, idx) =>
            idx < limit ? (
              <Tag key={idx} className="d-block my-1">
                {item.name} {item?.id ? `(${item?.id})` : ""}
              </Tag>
            ) : (
              ""
            ),
          )
        : ""}
      {list?.length > limit ? <Tag className="d-inline-block my-1"> + {list?.length - limit} ... </Tag> : ""}
    </>
  );
  LimitedTags.propTypes = {
    list: PropTypes.array,
    limit: PropTypes.number,
  };

  const columns = [
    {
      title: "No.",
      // width: "60px",
      render: (_, __, index) => <div>{(page - 1) * limit + index + 1}</div>,
    },
    {
      title: "Dashboard Name",
      render: (row) => row.name,
      maxWidth: "100px",
    },
    {
      title: "Dashboard Type",
      render: (row) => <div className="text-capitalize">{row.type || "Tile"}</div>,
      maxWidth: "100px",
      align: "center",
    },
    {
      title: "Business Name",
      render: (row) => <LimitedTags list={row?.business} />,
      maxWidth: "100px",
    },
    {
      title: "Site Location",
      render: (row) => <LimitedTags list={row?.site} />,
      // align: "center",
    },
    {
      title: "Created By",
      render: (row) => row?.createdBy?.name,
      align: "center",
    },
    {
      title: "Date Created",
      render: (row) => moment(row.createdAt).format("DD/MM/YYYY"),
      align: "center",
    },
    {
      title: "Default",
      align: "center",
      render: (row) => (
        <Input
          type="checkbox"
          checked={row?._id === activeDashboardId}
          onChange={() => makeDefaultHandler(userDetails._id, row._id, row?._id === activeDashboardId)}
        />
      ),
      // hide: userDetails?.role !== userRole.superAdmin,
    },
    {
      title: "Action",
      width: "190px",
      align: "center",
      render: (row) => (
        <div>
          <Link to={`/dashboard-list/${row._id}`}>
            <button className="btn btn-az-primary  me-1">
              <AiOutlineEye className="text-white" />
            </button>
          </Link>
          {/* EditDashboard */}
          {userDetails?.role !== userRole.public && userDetails?.role !== userRole.user && (
            <>
              <Link to={`/edit-dashboard/${row._id}`}>
                <button className="d-none d-md-inline btn btn-edit me-1">
                  <BiEdit />
                </button>
              </Link>
              <button
                className="d-none d-md-inline btn btn-view-gray me-1"
                title="Copy dashboard"
                onClick={() => copyHandler(row._id)}
              >
                <BiCopy />
              </button>
              <button className="me-2 btn btn-danger" onClick={() => deleteHandler(row._id)}>
                <MdDeleteOutline className="text-white" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ].filter((item) => !item?.hide);

  const copyHandler = async (id) => {
    try {
      Swal.fire({
        title: "Duplicate Dashboard?",
        text: "This action will open a copy of the dashboard for you to customize and save.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          navigate(`/edit-dashboard/${id}?cp=true`);
        }
      });
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  // handler
  const deleteHandler = (id) => {
    try {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you wish to delete this dashboard?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          const response = await axios.delete(`${endpoint.remove_dashboard}/${id}`, { withCredentials: true });
          if (response.status === 200) {
            Swal.fire("Deleted!", "Dashboard has been deleted.", "success");
            getDashboardList();
          }
        }
      });
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const makeDefaultHandler = async (userId, dashboardId, isActive) => {
    Swal.fire({
      title: "Are you sure?",
      text: isActive
        ? "This will remove this dashboard as default dashboard for your account."
        : "Make this dashboard as default dashboard for your account.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#5A5A5A",
      confirmButtonText: "Confirm",
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const body = { userId, dashboardId, isActive: !isActive };
          const { success } = await api.put(endpoint.active_dashboard, body);
          if (success) {
            getActiveDashboard();
            getDashboardList();
            Swal.fire({
              title: "Success!",
              text: "Default Dashboard Updated Successfully!",
              icon: "success",
              timer: 1500,
            });
          } else {
            Swal.fire({
              title: "Deleted!",
              text: "User has already default dashboard.",
              icon: "error",
              timer: 1500,
            });
          }
        }
      })
      .catch(() =>
        Swal.fire({
          title: "",
          text: "User has already default dashboard.",
          icon: "error",
          timer: 1500,
        }),
      );
  };

  return (
    <div className="px-md-3 mt-3">
      <div className="border p-3">
        {/* <h5>Dashboard List</h5> */}
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <label className="az-content-label">Manage Dashboard</label>
            <span className="d-block py-2">Dashboard List</span>
          </div>
          <div className="col-md-4 justify-content-end p-0 d-none d-md-flex">
            {userDetails?.role !== userRole.public && userDetails?.role !== userRole.user && !warnMsg && (
              <Button icon={<FaPlus />} type="primary" onClick={() => setMode("create")}>
                Create Dashboard
              </Button>
            )}
          </div>
        </div>
        <hr className="mt-0 mb-4" />
        {warnMsg && <p style={{ color: "red", textAlign: "center" }}>{warnMsg}</p>}
        <div className="row mb-2">
          <div className="col-md-10"></div>
          <div className="col-md-2">
            <Input
              type="search"
              size="large"
              value={searchText}
              onChange={(e) => {
                navigate("/dashboard-list");
                setSearchText(e.target.value);
              }}
              placeholder="Search"
              // className="me-2"
              aria-label="Search"
            />
          </div>
        </div>
        <Spin spinning={isLoading}>
          <DataTable
            columns={columns}
            dataSource={data}
            pagination={{
              pageSize: limit,
              current: page,
              total,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              pageSizeOptions: [10, 20, 50],
              responsive: true,
            }}
            onChange={(pagination) =>
              navigate(`/dashboard-list?page=${pagination.current}&limit=${pagination.pageSize}`)
            }
          />
        </Spin>

        <MetaDashboardModal show={mode === "create"} setShow={handleCloseModal} />
      </div>
    </div>
  );
};

export default DashboardList;
